import React from 'react';
import { useInView } from 'react-intersection-observer';
import { 
  FaSearch, FaCalendarAlt, FaRoad, FaMapMarkedAlt,
  FaCheckCircle 
} from 'react-icons/fa';

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      icon: <FaSearch />,
      title: 'Choose Your Caravan',
      description: 'Select from our 4-seater or 9-seater caravans based on your group size and requirements',
      color: 'from-accent-400 to-accent-600',
    },
    {
      icon: <FaCalendarAlt />,
      title: 'Plan Your Journey',
      description: 'Work with our team to customize your itinerary and select destinations across India',
      color: 'from-forest-400 to-forest-600',
    },
    {
      icon: <FaMapMarkedAlt />,
      title: 'Book & Confirm',
      description: 'Complete your booking with transparent pricing. Get confirmation and pre-trip briefing',
      color: 'from-primary-400 to-primary-600',
    },
    {
      icon: <FaRoad />,
      title: 'Hit the Road!',
      description: 'Your driver arrives with the caravan. Load up and start your unforgettable adventure',
      color: 'from-accent-500 to-forest-500',
    },
  ];

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <div
          ref={ref}
          className="text-center mb-16"
        >
          <span className="text-accent-600 font-semibold text-sm uppercase tracking-wide">
            How It Works
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mt-4 mb-6">
            Your Journey in
            <span className="text-accent-600"> 4 Simple Steps</span>
          </h2>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            From booking to adventure, we make the process seamless and enjoyable
          </p>
        </div>

        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-primary-200 -translate-y-1/2" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative"
              >
                {/* Step Card */}
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all card-hover border-2 border-primary-100">
                  {/* Step Number */}
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-accent-500 text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} text-white flex items-center justify-center text-3xl mb-4 shadow-lg`}
                  >
                    {step.icon}
                  </div>

                  <h3 className="text-xl font-bold text-primary-800 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-primary-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-primary-300 text-2xl z-10">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div
          className="mt-16 bg-gradient-to-r from-primary-600 via-forest-600 to-primary-700 rounded-3xl p-12 text-center text-white"
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Adventure?
          </h3>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your travel plans and get a customized quote
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#contact"
              className="px-8 py-4 bg-accent-500 text-white font-semibold rounded-full hover:bg-accent-600 shadow-lg"
            >
              Get Started
            </a>
            <a
              href="tel:+918380866111"
              className="px-8 py-4 bg-white text-primary-700 font-semibold rounded-full hover:bg-primary-50 shadow-lg"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
